import "./ClientDashboard.css";

import {
	AlertBannersList,
	DashboardHeader,
	HelpGuidesCard,
	LiveChatWidget,
	NewsUpdatesCard,
	PayrollClearingAccountCard,
	PayrollErrorsCard,
	PayrollProcessingCard,
	RecordsOfEmploymentCard,
	TaxRemittancesCard,
	YearEndComplianceCard,
} from "./panels";
import { BackdropCircularProgress, EnhancedDropDownRHFWithLabel, LabelPosition } from "@websential/cosmic-v20";
import { Box, Grid } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { setSelectedActivityLogActions, setSelectedStickyNavBar, setSelectedTaxYear } from "../../../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";

import { CountryService } from "../../../../services/common/CountryService";
import { DashboardGreeting } from "./panels/DashboardGreeting";
import { DashboardServices } from "../../../../services/common/DashboardServices";
import { IAppState } from "../../../../store/reducers";
import { YearService } from "../../../../services/YearService";
import { setCountryAndProvincesInfo } from "../../../../store/actions/countryAndProvincesInfoActions";
import { setSelectedDashboardApisData } from "../../../../store/actions/dashboardApisDataActions";
import { setSelectedDrawerOptionsMenu } from "../../../../store/actions/drawerMenuOptionsActions";
import { useDashboardOverview } from "./useDashboardOverview";
import { useNavigateToLogout } from "../../../../common/Common";
import { useQuery } from "@tanstack/react-query";

const useTaxYearByClient = (clientId: string) =>
	useQuery({
		queryKey: ["year", clientId],
		queryFn: () => YearService.getYearByClientId(clientId),
		refetchOnWindowFocus: false,
	});

const DASHBOARD_API_DATA_QUERY_KEY = "dashboard-api";
const useGetDashboardMenuData = (clientId: string, taxYear: number) =>
	useQuery({
		queryKey: [DASHBOARD_API_DATA_QUERY_KEY, clientId, taxYear],
		queryFn: () => DashboardServices.getDashboardMenuData(clientId, taxYear),
		refetchOnWindowFocus: false,
	});
const DASHBOARD_DATA_QUERY_KEY = "dashboard"
const useGetDashboardData = (clientId: string, taxYear: number) =>
	useQuery({
		queryKey: [DASHBOARD_DATA_QUERY_KEY, clientId, taxYear],
		queryFn: () => DashboardServices.getDashboardData(clientId, taxYear),
		refetchOnWindowFocus: false,
	});
export const ClientDashboard = () => {
	const dispatch = useDispatch();
	const { id } = useSelector((state: IAppState) => state.client);
	const { clientName } = useSelector((state: IAppState) => state.client);
	const drawerMenuOptions = useSelector((state: IAppState) => state.drawerMenu.menu);
	const profileName = useSelector(
		(state: any) =>
			`${state.user.profile.firstName} ${state.user.profile.lastName}`
	);
	const navigateToLogout = useNavigateToLogout();
	const [isYearLoading, setIsYearLoading] = useState(false);

	const { isPending: isLoadingYear, data: yearGet } = useTaxYearByClient(id);
	const currentYear: any = yearGet;

	const [year, setYear] = useState<any>(null);

	useEffect(() => {
		if (currentYear) {
			dispatch(setSelectedTaxYear({ year: currentYear.taxYear }));
			setYear(currentYear.taxYear);
		}
	}, [currentYear]);

	const { isPending: isPendingDashboardAPIData, data: dashboardApiData } = useGetDashboardMenuData(id, year);
	const { isPending: isPendingDashboardData, data: dashboardData } = useGetDashboardData(id, year);

	// TODO: when this dashboard is wired to a real multi-client account,
	// changing the selected client here should also trigger whatever your
	// app does to switch the active client context (e.g. dispatch a redux
	// action, update the route, or update `id` above) — this local state
	// only drives the header's own display for now.
	const [selectedClientId, setSelectedClientId] = useState<string>("");

	useEffect(() => {
		if (dashboardData?.header.selectedClientId) {
			setSelectedClientId(dashboardData.header.selectedClientId);
		}
	}, [dashboardData?.header.selectedClientId]);

	// ===== NEW: EnhancedDropDown's onSelectionChangeHandler passes the
	// selected item object (with a `value` field), not a raw string — copy
	// this whole handler =====
	const onClientSelectionChange = (e: any) => {
		setSelectedClientId(e.value);
	};

	const formMethods = useForm<any>({
		mode: "onBlur",
		reValidateMode: "onChange",
	});

	useEffect(() => {
		if (dashboardApiData && dashboardApiData.years_client_ddl) {
			dispatch(setSelectedTaxYear({ year: dashboardApiData.years_client_ddl.taxYear }));
			formMethods.setValue("year", dashboardApiData.years_client_ddl.taxYear);
		}
	}, [dashboardApiData]);

	const handleYearChange = async (event: any) => {
		try {
			setIsYearLoading(true);
			const response = await YearService.PutTaxYear(id, Number(event.label));
			if (response) {
				dispatch(setSelectedTaxYear({ year: response.taxYear }));
				setYear(response.taxYear);
			}
		} catch (err) {
			alert(err);
		} finally {
			setIsYearLoading(false);
		}
	};

	useEffect(() => {
		if (dashboardApiData) {
			if (dashboardApiData.countryDdl && Array.isArray(dashboardApiData.countryDdl)) {
				const canada = dashboardApiData.countryDdl.find(
					(item: any) => item.label === "Canada" || "canada"
				);
				if (canada) {
					(async () => {
						try {
							const response = await CountryService.getProvinceOrStateDDL(navigateToLogout, canada.value, "Province");
							dispatch(
								setCountryAndProvincesInfo({
									countryDDL: dashboardApiData.countryDdl,
									canada,
									canadaProvinces: response,
								})
							);
						} catch {
							// intentionally swallowed — province lookup is non-critical for the dashboard shell
						}
					})();
				}
			}
			dispatch(setSelectedDashboardApisData(dashboardApiData));
		}
	}, [dashboardApiData]);

	const expandedLabels = ["Home"];
	const expandedHomeDashboardOrDashboardItem = "Dashboard";
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (parentLabel === "Home" && item.label === expandedHomeDashboardOrDashboardItem) {
				isExpanded = true;
			}
			return {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedHomeDashboardOrDashboardItem && "/clients/dashboard",
			};
		});
	};

	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: "",
				entityId2: "",
				entityId3: "",
				screenName: "",
				accountantName: "",
				activityType: "",
				activityLogVisibility: false,
			})
		);
		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);

	const isLoading = isPendingDashboardData || isPendingDashboardAPIData || isYearLoading || isLoadingYear;

	return (
		<>
			<BackdropCircularProgress open={isLoading} testId="client-dashboard-backdrop" />

			<Box className="cd-page">
				{/* <DashboardHeader
					clients={dashboardData?.header.clients ?? []}
					selectedClientId={selectedClientId}
					onClientSelectionChange={onClientSelectionChange}
				>
					<Box className="cd-year-field">
						<FormProvider {...formMethods}>
							<form>
								<EnhancedDropDownRHFWithLabel
									selectName="year"
									label=""
									labelPosition={LabelPosition.Left}
									labelColumns={6}
									fieldColumns={6}
									testId="Filling-Year-list"
									items={dashboardApiData?.years_list ?? []}
									onSelectionChangeHandler={handleYearChange}
								/>
							</form>
						</FormProvider>
					</Box>
				</DashboardHeader> */}

				<DashboardGreeting
					name={profileName}
					companyName={clientName}
					actions={
						// Remove once the app bar year selector is wired to PutTaxYear
						// <Box className="cd-year-field">
						// 	<FormProvider {...formMethods}>
						// 		<form>
						// 			<EnhancedDropDownRHFWithLabel
						// 				selectName="year"
						// 				label=""
						// 				labelPosition={LabelPosition.Left}
						// 				labelColumns={6}
						// 				fieldColumns={6}
						// 				testId="Filling-Year-list"
						// 				items={dashboardApiData?.years_list ?? []}
						// 				onSelectionChangeHandler={handleYearChange}
						// 			/>
						// 		</form>
						// 	</FormProvider>
						// </Box>

						<></>
					}
				/>


				{dashboardData && <AlertBannersList data={dashboardData.alertBanners} />}
				{dashboardData && (
					<Grid container spacing={2.5} className="cd-main-grid">
						<Grid size={{ xs: 12, lg: 8 }}>
							<PayrollProcessingCard data={dashboardData.payrollProcessing} />
							<Grid container spacing={2.5}>
								<Grid size={{ xs: 12, sm: 6 }}>
									<Box className="cd-column">
										<PayrollErrorsCard data={dashboardData.payrollErrors} />
										<RecordsOfEmploymentCard data={dashboardData.recordsOfEmployment} />
									</Box>
								</Grid>
								<Grid size={{ xs: 12, sm: 6 }}>
									<Box className="cd-column">
										<YearEndComplianceCard data={dashboardData.yearEndCompliance} />
										<TaxRemittancesCard data={dashboardData.taxRemittances} />
									</Box>
								</Grid>
							</Grid>
						</Grid>
						<Grid size={{ xs: 12, lg: 4 }}>
							<Box className="cd-column">
								<PayrollClearingAccountCard data={dashboardData.payrollClearingAccount} />
								<NewsUpdatesCard data={dashboardData.news} />
								<HelpGuidesCard data={dashboardData.helpGuides} />
							</Box>
						</Grid>
					</Grid>
				)}

				<LiveChatWidget
					support={
						dashboardData?.support ?? {
							phoneNumbers: [],
							responseTimeNote: "",
							chatTitle: "Live chat",
							chatStatusLabel: "",
							initialMessages: [],
						}
					}
				/>
			</Box>
		</>
	);
};
